import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { humanDuration, queueExplanation } from "./formatting";
import type { ShellServices } from "./services";

const QUEUE_STATE_LABELS: Record<string, string> = {
  healthy: "В норме",
  lagging: "Отстаёт",
  stalled: "Стоит",
};

function badgeModifier(status: string | undefined): string {
  return status === "healthy" || status === "ready" ? "ready" : "rejected";
}

function Health({ title, status, statusLabel, children }: { title: string; status: string | undefined; statusLabel?: string; children: ReactNode }) {
  return (
    <section className="health-card">
      <h2>{title}</h2>
      <p className={`status-badge status-badge--${badgeModifier(status)}`}>{statusLabel ?? status ?? "Недоступно"}</p>
      {children}
    </section>
  );
}

export function OperationsPage({ services }: { services: ShellServices }) {
  const build = useQuery({ queryKey: ["operations", "build"], queryFn: () => services.operations!.build(), retry: false });
  const readiness = useQuery({ queryKey: ["operations", "readiness"], queryFn: () => services.operations!.readiness(), retry: false });
  const queue = useQuery({ queryKey: ["operations", "queue"], queryFn: () => services.operations!.queueHealth(), retry: false });
  const outbox = useQuery({ queryKey: ["operations", "outbox"], queryFn: () => services.operations!.outboxHealth(), retry: false });
  const loading = build.isLoading || readiness.isLoading || queue.isLoading || outbox.isLoading;
  const queues = queue.data?.data.queues ?? [];
  const troubled = queues.filter((item) => item.status !== "healthy");

  return (
    <main className="content-page" aria-labelledby="operations-title">
      <header className="page-header">
        <div>
          <p className="eyebrow">Эксплуатация</p>
          <h1 id="operations-title">Состояние системы</h1>
          <p>Показаны только предназначенные для ERP статусы. Внутренние метрики и диагностические данные не раскрываются.</p>
        </div>
      </header>
      {loading ? (
        <p aria-busy="true">Обновляем состояние системы…</p>
      ) : (
        <>
          <div className="health-grid">
            <Health title="Сборка" status={build.data ? "ready" : undefined} statusLabel={build.data ? "Развёрнута" : undefined}>
              {build.data ? (
                <dl>
                  <div>
                    <dt>Версия</dt>
                    <dd>{build.data.version}</dd>
                  </div>
                  <div>
                    <dt>Ревизия</dt>
                    <dd>
                      <code>{build.data.commit.slice(0, 12)}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Собрано</dt>
                    <dd>{build.data.built_at === "unknown" ? "Дата сборки не проставлена" : build.data.built_at}</dd>
                  </div>
                </dl>
              ) : (
                <p>Сведения о сборке недоступны.</p>
              )}
            </Health>
            <Health
              title="Готовность"
              status={readiness.data?.status}
              statusLabel={readiness.data ? (readiness.data.status === "ready" ? "Готова" : "Не готова") : undefined}
            >
              {readiness.data ? (
                <dl>
                  <div>
                    <dt>PostgreSQL</dt>
                    <dd>{readiness.data.checks.postgresql ? "Доступна" : "Недоступна"}</dd>
                  </div>
                  <div>
                    <dt>Кэш</dt>
                    <dd>{readiness.data.checks.cache ? "Доступен" : "Недоступен"}</dd>
                  </div>
                </dl>
              ) : (
                <p>Проверка готовности не выполнена.</p>
              )}
            </Health>
            <Health
              title="Фоновые задачи"
              status={queue.data?.data.status}
              statusLabel={
                queue.data ? (queue.data.data.status === "healthy" ? "В норме" : "Есть отставание") : undefined
              }
            >
              {queue.data ? (
                <dl>
                  <div>
                    <dt>Ожидают</dt>
                    <dd>{queue.data.data.queued}</dd>
                  </div>
                  <div>
                    <dt>Отставание</dt>
                    <dd>{humanDuration(queue.data.data.lag_seconds)}</dd>
                  </div>
                  <div>
                    <dt>Ошибки за сутки</dt>
                    <dd>{queue.data.data.failed_recent}</dd>
                  </div>
                </dl>
              ) : (
                <p>Статус фоновых задач недоступен.</p>
              )}
            </Health>
            <Health
              title="Outbox"
              status={outbox.data?.data.status}
              statusLabel={outbox.data ? (outbox.data.data.status === "healthy" ? "В норме" : "Есть ошибки") : undefined}
            >
              {outbox.data ? (
                <dl>
                  <div>
                    <dt>Ожидают</dt>
                    <dd>{outbox.data.data.pending}</dd>
                  </div>
                  <div>
                    <dt>Ошибок</dt>
                    <dd>{outbox.data.data.failed}</dd>
                  </div>
                  <div>
                    <dt>Самое старое</dt>
                    <dd>{outbox.data.data.oldest_pending_at ?? "—"}</dd>
                  </div>
                </dl>
              ) : (
                <p>Статус outbox недоступен.</p>
              )}
            </Health>
          </div>

          {queue.data ? (
            <section className="dw-panel" aria-labelledby="operations-queues-title">
              <h2 id="operations-queues-title">Очереди</h2>
              {troubled.length === 0 ? (
                <p>Все очереди разбираются вовремя.</p>
              ) : (
                <p>
                  Отстают: {troubled.map((item) => item.label.toLowerCase()).join(", ")}. Обычно это значит, что внешняя
                  система отвечает медленно либо обработчик не запущен.
                </p>
              )}
              <div className="dw-table-wrap">
                <table className="dw-table">
                  <thead>
                    <tr>
                      <th scope="col">Очередь</th>
                      <th scope="col">Ожидают</th>
                      <th scope="col">Старейшая</th>
                      <th scope="col">Состояние</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queues.map((item) => (
                      <tr key={item.queue}>
                        <td data-label="Очередь">
                          {item.label}
                          <span className="dw-secondary" title={`Техническое имя очереди: ${item.queue}`}>
                            {item.queue}
                          </span>
                        </td>
                        <td data-label="Ожидают">{item.depth}</td>
                        <td data-label="Старейшая">
                          {item.oldest_age_seconds === 0 ? "—" : humanDuration(item.oldest_age_seconds)}
                        </td>
                        <td data-label="Состояние">
                          <span className={`status-badge status-badge--${item.status === "healthy" ? "ready" : item.status === "lagging" ? "pending" : "rejected"}`}>
                            {QUEUE_STATE_LABELS[item.status] ?? item.status}
                          </span>
                          <span className="dw-secondary">{queueExplanation(item)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="dw-secondary">
                Ошибок за последние сутки: {queue.data.data.failed_recent}. Всего за всё время: {queue.data.data.failed}.
                Накопленный счётчик хранит историю с момента запуска стенда и о текущем состоянии не говорит ничего;
                смотреть надо на суточный. Разбор конкретных ошибок — в журнале сервиса фоновых задач.
              </p>
            </section>
          ) : null}
        </>
      )}
    </main>
  );
}
